import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Alert, Snackbar,
  Chip, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem,
  Accordion, AccordionSummary, AccordionDetails
} from "@mui/material";
import {
  Book, Add, Refresh, Visibility, Delete, Undo, FileDownload, 
  FilterList, ExpandMore
} from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function LibroDiario() {
  const [asientos, setAsientos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [centrosCostos, setCentrosCostos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detalleDialogOpen, setDetalleDialogOpen] = useState(false);
  const [asientoSeleccionado, setAsientoSeleccionado] = useState(null);
  const [detallesAsiento, setDetallesAsiento] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    fecha_desde: "",
    fecha_hasta: "",
    tipo_comprobante: "",
    estado: "",
    glosa: ""
  });
  
  const [form, setForm] = useState({
    fecha_transaccion: new Date().toISOString().split("T")[0],
    glosa: "",
    estado: "BORRADOR"
  });
  
  const [detalles, setDetalles] = useState([
    { cuenta_id: "", ruc_tercero: "", debe: "", haber: "", centro_costo_id: "" }
  ]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    cargarAsientos();
    cargarCuentas();
    cargarCentrosCostos();
  }, []);

  const cargarCentrosCostos = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/centro-costos`, { headers: getAuthHeaders() });
      const todos = Array.isArray(res.data) ? res.data : [];
      // Mostrar activos primero, pero incluir todos para que el selector funcione
      setCentrosCostos(todos.filter(c => c.activo !== false));
    } catch (e) {
      console.warn('Centro costos no disponible:', e.message);
    }
  };

  const cargarAsientos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde);
      if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta);
      if (filtros.tipo_comprobante) params.append('tipo_comprobante_sri', filtros.tipo_comprobante);
      if (filtros.estado) params.append('estado', filtros.estado);
      if (filtros.glosa) params.append('glosa', filtros.glosa);

      const res = await axios.get(`${API_URL}/api/contabilidad/asientos?${params}`, {
        headers: getAuthHeaders()
      });
      setAsientos(res.data);
    } catch (error) {
      showSnackbar("Error al cargar asientos", "error");
    } finally {
      setLoading(false);
    }
  };

  const cargarCuentas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contabilidad/plan-cuentas`, {
        headers: getAuthHeaders()
      });
      setCuentas(res.data.filter(c => c.acepta_movimiento));
    } catch (error) {
      console.error("Error al cargar cuentas:", error);
    }
  };

  const verDetalles = async (asientoId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/contabilidad/asientos/${asientoId}/detalles`,
        { headers: getAuthHeaders() }
      );
      setDetallesAsiento(res.data);
      setAsientoSeleccionado(asientos.find(a => a.id === asientoId));
      setDetalleDialogOpen(true);
    } catch (error) {
      showSnackbar("Error al cargar detalles", "error");
    }
  };

  const agregarDetalle = () => {
    setDetalles([...detalles, { cuenta_id: "", ruc_tercero: "", debe: "", haber: "", centro_costo_id: "" }]);
  };

  const eliminarDetalle = (index) => {
    setDetalles(detalles.filter((_, i) => i !== index));
  };

  const actualizarDetalle = (index, campo, valor) => {
    const nuevosDetalles = [...detalles];
    nuevosDetalles[index][campo] = valor;
    setDetalles(nuevosDetalles);
  };

  const calcularTotales = () => {
    const totalDebe = detalles.reduce((sum, d) => sum + parseFloat(d.debe || 0), 0);
    const totalHaber = detalles.reduce((sum, d) => sum + parseFloat(d.haber || 0), 0);
    return { totalDebe, totalHaber, diferencia: totalDebe - totalHaber };
  };

  const crearAsiento = async () => {
    const { totalDebe, totalHaber, diferencia } = calcularTotales();
    
    if (Math.abs(diferencia) > 0.01) {
      showSnackbar(`Error de partida doble: Debe (${totalDebe.toFixed(2)}) ≠ Haber (${totalHaber.toFixed(2)})`, "error");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/contabilidad/asientos`,
        { asiento: form, detalles },
        { headers: getAuthHeaders() }
      );
      showSnackbar("Asiento creado correctamente", "success");
      setDialogOpen(false);
      resetForm();
      cargarAsientos();
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Error al crear asiento", "error");
    }
  };

  const anularAsiento = async (id) => {
    if (!window.confirm("¿Está seguro de anular este asiento?")) return;

    try {
      await axios.put(
        `${API_URL}/api/contabilidad/asientos/${id}/anular`,
        {},
        { headers: getAuthHeaders() }
      );
      showSnackbar("Asiento anulado", "success");
      cargarAsientos();
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Error al anular", "error");
    }
  };

  const reversarAsiento = async (id) => {
    const glosa = prompt("Ingrese la glosa para el asiento de reversión:");
    if (!glosa) return;

    try {
      await axios.post(
        `${API_URL}/api/contabilidad/asientos/${id}/reversar`,
        { glosa },
        { headers: getAuthHeaders() }
      );
      showSnackbar("Asiento reversado correctamente", "success");
      cargarAsientos();
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Error al reversar", "error");
    }
  };

  const resetForm = () => {
    setForm({
      fecha_transaccion: new Date().toISOString().split("T")[0],
      glosa: "",
      estado: "BORRADOR"
    });
    setDetalles([{ cuenta_id: "", ruc_tercero: "", debe: "", haber: "", centro_costo_id: "" }]);
  };

  const exportarExcel = () => {
    // Preparar datos para exportar
    const datosExport = [];
    
    asientos.forEach(asiento => {
      if (asiento.detalles && asiento.detalles.length > 0) {
        asiento.detalles.forEach(detalle => {
          const cuenta = cuentas.find(c => c.id === detalle.cuenta_id);
          datosExport.push({
            'Fecha': new Date(asiento.fecha_transaccion).toLocaleDateString(),
            'Glosa': asiento.glosa,
            'Comprobante': asiento.num_comprobante_sri || '',
            'Tipo': asiento.tipo_comprobante_sri || '',
            'Cuenta Código': cuenta?.codigo || '',
            'Cuenta Nombre': cuenta?.nombre || '',
            'RUC Tercero': detalle.ruc_tercero || '',
            'Debe': parseFloat(detalle.debe || 0).toFixed(2),
            'Haber': parseFloat(detalle.haber || 0).toFixed(2),
            'Estado': asiento.estado
          });
        });
      } else {
        // Si no tiene detalles, mostrar solo el asiento
        datosExport.push({
          'Fecha': new Date(asiento.fecha_transaccion).toLocaleDateString(),
          'Glosa': asiento.glosa,
          'Comprobante': asiento.num_comprobante_sri || '',
          'Tipo': asiento.tipo_comprobante_sri || '',
          'Cuenta Código': '',
          'Cuenta Nombre': '',
          'RUC Tercero': '',
          'Debe': '',
          'Haber': '',
          'Estado': asiento.estado
        });
      }
    });

    // Crear CSV
    const headers = Object.keys(datosExport[0] || {});
    const csvContent = [
      headers.join(','),
      ...datosExport.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
    ].join('\n');

    // Descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `libro_diario_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportarPDF = () => {
    // Crear contenido HTML para PDF
    const htmlContent = `
      <html>
        <head>
          <title>Libro Diario</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .header { text-align: center; margin-bottom: 20px; }
            .totals { margin-top: 20px; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>LIBRO DIARIO</h2>
            <p>Fecha de generación: ${new Date().toLocaleDateString()}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Glosa</th>
                <th>Comprobante</th>
                <th>Cuenta</th>
                <th>Debe</th>
                <th>Haber</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              ${asientos.map(asiento => {
                if (asiento.detalles && asiento.detalles.length > 0) {
                  return asiento.detalles.map(detalle => {
                    const cuenta = cuentas.find(c => c.id === detalle.cuenta_id);
                    return `
                      <tr>
                        <td>${new Date(asiento.fecha_transaccion).toLocaleDateString()}</td>
                        <td>${asiento.glosa}</td>
                        <td>${asiento.num_comprobante_sri || ''}</td>
                        <td>${cuenta?.codigo || ''} - ${cuenta?.nombre || ''}</td>
                        <td style="text-align: right;">${parseFloat(detalle.debe || 0).toFixed(2)}</td>
                        <td style="text-align: right;">${parseFloat(detalle.haber || 0).toFixed(2)}</td>
                        <td>${asiento.estado}</td>
                      </tr>
                    `;
                  }).join('');
                }
                return `
                  <tr>
                    <td>${new Date(asiento.fecha_transaccion).toLocaleDateString()}</td>
                    <td>${asiento.glosa}</td>
                    <td>${asiento.num_comprobante_sri || ''}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>${asiento.estado}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    // Abrir en nueva ventana para imprimir/guardar como PDF
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getEstadoChip = (estado) => {
    const estados = {
      BORRADOR: { color: "default", label: "BORRADOR" },
      CONTABILIZADO: { color: "success", label: "CONTABILIZADO" },
      ANULADO: { color: "error", label: "ANULADO" }
    };
    const config = estados[estado] || { color: "default", label: estado };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const { totalDebe, totalHaber, diferencia } = calcularTotales();

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Book />
            <Typography variant="h5" fontWeight={700}>
              Libro Diario
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={exportarExcel}
              disabled={asientos.length === 0}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={exportarPDF}
              disabled={asientos.length === 0}
            >
              PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={cargarAsientos}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setDialogOpen(true)}
            >
              Nuevo Asiento
            </Button>
          </Box>
        </Box>

        {/* Filtros */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterList />
              <Typography>Filtros de Búsqueda</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha Desde"
                  value={filtros.fecha_desde}
                  onChange={(e) => setFiltros({...filtros, fecha_desde: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha Hasta"
                  value={filtros.fecha_hasta}
                  onChange={(e) => setFiltros({...filtros, fecha_hasta: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Tipo Comprobante</InputLabel>
                  <Select
                    value={filtros.tipo_comprobante}
                    label="Tipo Comprobante"
                    onChange={(e) => setFiltros({...filtros, tipo_comprobante: e.target.value})}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="01">Facturas</MenuItem>
                    <MenuItem value="03">Liquidaciones</MenuItem>
                    <MenuItem value="04">Notas de Crédito</MenuItem>
                    <MenuItem value="COMPRA">Compras</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filtros.estado}
                    label="Estado"
                    onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="BORRADOR">Borrador</MenuItem>
                    <MenuItem value="CONTABILIZADO">Contabilizado</MenuItem>
                    <MenuItem value="ANULADO">Anulado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={cargarAsientos}
                  sx={{ height: '56px' }}
                >
                  Aplicar Filtros
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {loading ? (
          <Typography>Cargando...</Typography>
        ) : asientos.length === 0 ? (
          <Alert severity="info">
            No hay asientos contables registrados.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell><strong>Fecha</strong></TableCell>
                  <TableCell><strong>Glosa</strong></TableCell>
                  <TableCell><strong>Comprobante</strong></TableCell>
                  <TableCell align="right"><strong>Debe</strong></TableCell>
                  <TableCell align="right"><strong>Haber</strong></TableCell>
                  <TableCell><strong>Estado</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {asientos.map((asiento) => (
                  <TableRow key={asiento.id} hover>
                    <TableCell>{new Date(asiento.fecha_transaccion).toLocaleDateString()}</TableCell>
                    <TableCell>{asiento.glosa}</TableCell>
                    <TableCell>{asiento.num_comprobante_sri || "—"}</TableCell>
                    <TableCell align="right">${parseFloat(asiento.total_debe || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">${parseFloat(asiento.total_haber || 0).toFixed(2)}</TableCell>
                    <TableCell>{getEstadoChip(asiento.estado)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver Detalles">
                        <IconButton size="small" onClick={() => verDetalles(asiento.id)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {asiento.estado === "BORRADOR" && (
                        <Tooltip title="Anular">
                          <IconButton size="small" color="error" onClick={() => anularAsiento(asiento.id)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                      {asiento.estado === "CONTABILIZADO" && (
                        <Tooltip title="Reversar">
                          <IconButton size="small" color="warning" onClick={() => reversarAsiento(asiento.id)}>
                            <Undo />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: "#eeeeee" }}>
                  <TableCell colSpan={3}><strong>TOTALES</strong></TableCell>
                  <TableCell align="right">
                    <strong>${asientos.reduce((s, a) => s + parseFloat(a.total_debe || 0), 0).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>${asientos.reduce((s, a) => s + parseFloat(a.total_haber || 0), 0).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog Crear Asiento */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Nuevo Asiento Contable</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Fecha"
                value={form.fecha_transaccion}
                onChange={(e) => setForm({ ...form, fecha_transaccion: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={form.estado}
                  label="Estado"
                  onChange={(e) => setForm({ ...form, estado: e.target.value })}
                >
                  <MenuItem value="BORRADOR">BORRADOR</MenuItem>
                  <MenuItem value="CONTABILIZADO">CONTABILIZADO</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Glosa"
                value={form.glosa}
                onChange={(e) => setForm({ ...form, glosa: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Detalles del Asiento
          </Typography>

          {detalles.map((detalle, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: index % 2 === 0 ? '#fafafa' : '#fff' }}>
              {/* Fila 1: Cuenta + Debe + Haber + Eliminar */}
              <Grid container spacing={2} sx={{ mb: centrosCostos.length > 0 ? 1.5 : 0 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Cuenta *</InputLabel>
                    <Select
                      value={detalle.cuenta_id}
                      label="Cuenta *"
                      onChange={(e) => actualizarDetalle(index, "cuenta_id", e.target.value)}
                    >
                      {cuentas.map(c => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.codigo} - {c.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Debe"
                    type="number"
                    value={detalle.debe}
                    onChange={(e) => actualizarDetalle(index, "debe", e.target.value)}
                    onFocus={e => { if (+e.target.value === 0) e.target.value = ""; }}
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Haber"
                    type="number"
                    value={detalle.haber}
                    onChange={(e) => actualizarDetalle(index, "haber", e.target.value)}
                    onFocus={e => { if (+e.target.value === 0) e.target.value = ""; }}
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => eliminarDetalle(index)}
                    disabled={detalles.length === 1}
                    sx={{ height: 40 }}
                  >
                    Eliminar
                  </Button>
                </Grid>
              </Grid>

              {/* Fila 2: RUC Tercero + Centro de Costos */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="RUC / Cédula Tercero"
                    value={detalle.ruc_tercero}
                    onChange={(e) => actualizarDetalle(index, "ruc_tercero", e.target.value)}
                    inputProps={{ maxLength: 13 }}
                    placeholder="Opcional"
                  />
                </Grid>
                {centrosCostos.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Centro de Costos</InputLabel>
                      <Select
                        value={detalle.centro_costo_id || ""}
                        label="Centro de Costos"
                        onChange={(e) => actualizarDetalle(index, "centro_costo_id", e.target.value)}
                      >
                        <MenuItem value="">Sin asignar</MenuItem>
                        {centrosCostos.map(cc => (
                          <MenuItem key={cc.id} value={cc.id}>
                            {cc.codigo} — {cc.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={agregarDetalle}
            sx={{ mb: 2 }}
          >
            Agregar Línea
          </Button>

          <Paper sx={{ p: 2, bgcolor: diferencia === 0 ? "#e8f5e9" : "#ffebee" }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="body2">Total Debe:</Typography>
                <Typography variant="h6">${totalDebe.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">Total Haber:</Typography>
                <Typography variant="h6">${totalHaber.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2">Diferencia:</Typography>
                <Typography variant="h6" color={diferencia === 0 ? "success.main" : "error.main"}>
                  ${diferencia.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={crearAsiento} variant="contained" disabled={Math.abs(diferencia) > 0.01}>
            Crear Asiento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Ver Detalles */}
      <Dialog open={detalleDialogOpen} onClose={() => setDetalleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalles del Asiento</DialogTitle>
        <DialogContent>
          {asientoSeleccionado && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">Fecha: {new Date(asientoSeleccionado.fecha_transaccion).toLocaleDateString()}</Typography>
              <Typography variant="body2">Glosa: {asientoSeleccionado.glosa}</Typography>
              <Typography variant="body2">Estado: {getEstadoChip(asientoSeleccionado.estado)}</Typography>
            </Box>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell><strong>Cuenta</strong></TableCell>
                  <TableCell><strong>Centro Costo</strong></TableCell>
                  <TableCell><strong>RUC Tercero</strong></TableCell>
                  <TableCell align="right"><strong>Debe</strong></TableCell>
                  <TableCell align="right"><strong>Haber</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detallesAsiento.map((detalle) => {
                  const cuenta = cuentas.find(c => c.id === detalle.cuenta_id);
                  const cc = centrosCostos.find(c => c.id === detalle.centro_costo_id);
                  return (
                    <TableRow key={detalle.id}>
                      <TableCell>
                        {cuenta ? `${cuenta.codigo} - ${cuenta.nombre}` : detalle.cuenta_id}
                      </TableCell>
                      <TableCell>
                        {cc ? <Chip label={`${cc.codigo} - ${cc.nombre}`} size="small" variant="outlined" color="primary" /> : "—"}
                      </TableCell>
                      <TableCell>{detalle.ruc_tercero || "—"}</TableCell>
                      <TableCell align="right">${parseFloat(detalle.debe).toFixed(2)}</TableCell>
                      <TableCell align="right">${parseFloat(detalle.haber).toFixed(2)}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={3}><strong>TOTALES</strong></TableCell>
                  <TableCell align="right">
                    <strong>
                      ${detallesAsiento.reduce((sum, d) => sum + parseFloat(d.debe), 0).toFixed(2)}
                    </strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>
                      ${detallesAsiento.reduce((sum, d) => sum + parseFloat(d.haber), 0).toFixed(2)}
                    </strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalleDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
